import { type Page } from '@/types/store';
import { formatCurrency } from '@/lib/currency';
import { createIconElement, PRODUCT_TYPE_LABELS } from '@/lib';
import { stripHtmlTags } from '@/lib/helpers';
import IcImage from '@/assets/icons/ic-image.svg';
import { getFunnelPageIcon } from '../../../logic';

interface SelectedProductPreviewProps {
    page: Page;
    /** Overrides store price when set — renders store price struck through and funnel price next to it. */
    funnelPrice?: number | null;
}

export const SelectedProductPreview = ({ page, funnelPrice }: SelectedProductPreviewProps) => {
    const product = page.data?.product;
    const title = product?.title?.trim() || 'Untitled Product';
    const description = product?.description ? stripHtmlTags(product.description) : '';
    const image = product?.image;
    const typeLabel = PRODUCT_TYPE_LABELS[page.type as keyof typeof PRODUCT_TYPE_LABELS] ?? null;
    const currency = product?.price?.currency ?? 'USD';
    const storePriceLabel =
        typeof product?.price?.amount === 'number' ? formatCurrency(product.price.amount, currency) : null;
    const funnelPriceLabel = typeof funnelPrice === 'number' ? formatCurrency(funnelPrice, currency) : null;
    const hasPriceOverride = funnelPriceLabel !== null && funnelPriceLabel !== storePriceLabel;
    const typeIcon = createIconElement(getFunnelPageIcon(page.type), { className: 'size-4 shrink-0' });

    return (
        <div className='border-neutral-subtle flex items-start gap-3 rounded-md border p-3 md:gap-4 md:p-4'>
            <div className='border-neutral-subtle flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md border md:size-16'>
                {image ? (
                    <img src={image} alt='' className='size-full object-cover' />
                ) : (
                    <IcImage className='text-light size-6' />
                )}
            </div>
            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                <div className='flex flex-col items-start justify-between gap-1 md:flex-row md:gap-3'>
                    <span className='text-heading-h4 text-dark truncate'>{title}</span>
                    {storePriceLabel && (
                        <span className='flex shrink-0 items-baseline gap-2'>
                            {hasPriceOverride && (
                                <span className='text-body-sm text-light line-through'>{storePriceLabel}</span>
                            )}
                            <span className='text-body-sm text-dark font-medium'>
                                {hasPriceOverride ? funnelPriceLabel : storePriceLabel}
                            </span>
                        </span>
                    )}
                </div>
                {typeLabel && (
                    <span className='text-body-sm text-gray flex items-center gap-1.5'>
                        {typeIcon}
                        {typeLabel}
                    </span>
                )}
                {description && <p className='text-body-sm text-gray line-clamp-2'>{description}</p>}
            </div>
        </div>
    );
};
